import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useUpdateProfile, useVendorProfile } from "@/api";
import {
  draftFromProfile,
  draftToPayload,
  EMPTY_ONBOARDING_DRAFT,
  type OnboardingDraft,
} from "@/constants/onboarding-steps";

interface OnboardingFormContextValue {
  draft: OnboardingDraft;
  logoUrl: string | null;
  updateDraft: (patch: Partial<OnboardingDraft>) => void;
  saveDraft: (patch?: Partial<OnboardingDraft>) => Promise<void>;
  clearLogo: () => Promise<void>;
  isSaving: boolean;
  isLoading: boolean;
}

const OnboardingFormContext = createContext<OnboardingFormContextValue | null>(null);

export function OnboardingFormProvider({ children }: { children: ReactNode }) {
  const profileQuery = useVendorProfile();
  const updateProfile = useUpdateProfile();
  const [draft, setDraft] = useState<OnboardingDraft>(EMPTY_ONBOARDING_DRAFT);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    if (profileQuery.data && !hydrated) {
      setDraft(draftFromProfile(profileQuery.data));
      setHydrated(true);
    }
  }, [profileQuery.data, hydrated]);

  const updateDraft = useCallback((patch: Partial<OnboardingDraft>) => {
    setDraft((current) => ({ ...current, ...patch }));
  }, []);

  const saveDraft = useCallback(
    async (patch?: Partial<OnboardingDraft>) => {
      const next = patch ? { ...draft, ...patch } : draft;
      if (patch) setDraft(next);
      await updateProfile.mutateAsync(draftToPayload(next));
    },
    [draft, updateProfile],
  );

  const clearLogo = useCallback(async () => {
    setDraft((current) => ({ ...current, logoFileId: "" }));
    await updateProfile.mutateAsync({ logoFileId: null });
  }, [updateProfile]);

  const value = useMemo(
    () => ({
      draft,
      logoUrl: profileQuery.data?.logoUrl ?? null,
      updateDraft,
      saveDraft,
      clearLogo,
      isSaving: updateProfile.isPending,
      isLoading: profileQuery.isLoading,
    }),
    [
      draft,
      profileQuery.data?.logoUrl,
      updateDraft,
      saveDraft,
      clearLogo,
      updateProfile.isPending,
      profileQuery.isLoading,
    ],
  );

  return <OnboardingFormContext.Provider value={value}>{children}</OnboardingFormContext.Provider>;
}

export function useOnboardingForm() {
  const context = useContext(OnboardingFormContext);
  if (!context) {
    throw new Error("useOnboardingForm must be used within OnboardingFormProvider");
  }
  return context;
}
